/**
 * breakpoints.ts — ONE source of truth for every responsive threshold used in JS.
 *
 * Tailwind v4 declares its screens in CSS (`sm 640 · md 768 · lg 1024 · xl 1280 · 2xl 1536`) and this
 * repo does NOT override them (`src/index.css` has no `--breakpoint-*`), so the numbers here MIRROR
 * the utilities written in class names. Two consequences, both deliberate:
 *
 *   1. Never write a raw `768` in a `matchMedia(...)` or a width comparison — import the token. A
 *      hook carrying its own copy of the scale is a hook that can silently drift behind the CSS it
 *      is supposed to agree with (RULES_FE_QA 🛡 responsive preflight, check 1).
 *   2. `queryDown()` subtracts one pixel. `max-width: 768px` and Tailwind's `md:` (`min-width:
 *      768px`) overlap by exactly one pixel, and at that pixel two layouts are both "correct" — so
 *      the boundary belongs to the wider layout only, and the two conditions never both match.
 *
 * CSS cannot import a TypeScript module, so the ONE media query that must stay in CSS
 * (`src/index.css`, the mobile base-size step) names this token in a comment AND its numeric parity
 * is asserted by `scripts/responsive_preflight.sh`. A comment alone would be a promise; the script
 * makes it a check.
 *
 * STRUCTURAL SWITCH POINT (preflight check 4): `SHELL_SWITCH_PX` is where the CRM shell changes
 * identity — at or above it the Nexus desktop shell renders, below it `MobileAdmin`. `useIsMobile()`
 * reads this token and nothing else, so "which shell am I in?" (JS) and "which shell does the CSS
 * style?" (CSS) cannot disagree. A route registered in only one of the two shells is a KNOWN
 * product gap and is recorded, never papered over (RULES_FE_QA learning 28).
 */

export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

export type BreakpointName = keyof typeof BREAKPOINTS;

/** The desktop ↔ mobile shell boundary. */
export const SHELL_SWITCH_PX = BREAKPOINTS.md;

/** `(min-width: Npx)` — the same condition Tailwind's `sm:` / `md:` / … utilities emit. */
export const queryUp = (px: number): string => `(min-width: ${px}px)`;

/** `(max-width: N-1px)` — strictly below a breakpoint, so it never overlaps `queryUp`. */
export const queryDown = (px: number): string => `(max-width: ${px - 1}px)`;

/** The named form: is the viewport narrower than `name`? */
export const queryDownName = (name: BreakpointName): string => queryDown(BREAKPOINTS[name]);

/** Imperative read for call sites that only need a boolean at one moment. */
export const innerWidthBelow = (name: BreakpointName): boolean =>
  typeof window !== "undefined" && window.innerWidth < BREAKPOINTS[name];
